import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import DramaCard from '@/components/base/DramaCard';
import { useSEO } from '@/hooks/useSEO';
import { dramas } from '@/mocks/dramas';
import { categories } from '@/mocks/categories';

type SortKey = 'heat' | 'latest';

export default function DramasPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get('cat') || 'all';
  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState<SortKey>('heat');

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '瓜田总览',
      inLanguage: 'zh-CN',
      description: '西瓜黑料全部事件的分类列表，可筛选与搜索。',
    }),
    [],
  );

  useSEO({
    title: '瓜田总览 — 全部事件时间线一览 | 西瓜黑料',
    description:
      '在瓜田总览里按娱乐圈、网红翻车、商业互撕、科技圈、综艺影视分类筛选，或直接搜索关键词，按热度与最新排序，快速找到你要追的那条时间线。',
    keywords: '西瓜黑料,瓜田总览,西瓜黑料时间线,吃瓜事件列表,西瓜黑料反转',
    path: '/dramas',
    jsonLd,
  });

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = dramas.filter((drama) => {
      const catOk = activeCat === 'all' || drama.category === activeCat;
      if (!catOk) return false;
      if (!kw) return true;
      const haystack = `${drama.title} ${drama.summary} ${drama.tags.join(' ')}`.toLowerCase();
      return haystack.includes(kw);
    });
    return list.sort((a, b) =>
      sort === 'heat'
        ? b.heat - a.heat
        : a.updatedAt < b.updatedAt
          ? 1
          : a.updatedAt > b.updatedAt
            ? -1
            : 0,
    );
  }, [activeCat, keyword, sort]);

  const setCat = (catId: string) => {
    if (catId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat: catId });
    }
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 pb-10 pt-24 md:px-8 md:pb-14 md:pt-28">
        <div className="mx-auto max-w-6xl">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '瓜田总览' }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            瓜田总览
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            这里收录了站内所有在追的事件，每条都配一条完整时间线。按分类筛选，或直接搜索关键词，找到你想接着追的那一出。
          </p>
        </div>
      </section>

      <section className="px-4 py-8 md:px-8 md:py-10">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCat('all')}
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                  activeCat === 'all'
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCat(cat.id)}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
                    activeCat === cat.id
                      ? 'border-primary-500 bg-primary-500 text-background-50'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索事件、标签…"
                  className="h-10 w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 sm:w-64"
                />
              </div>
              <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setSort('heat')}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === 'heat'
                      ? 'bg-accent-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按热度
                </button>
                <button
                  type="button"
                  onClick={() => setSort('latest')}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sort === 'latest'
                      ? 'bg-accent-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  按最新
                </button>
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="font-medium text-foreground-900">{filtered.length}</span> 条时间线
          </p>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
          ) : (
            <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-background-400"></i>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                这一口瓜还没长出来
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                没找到匹配的事件，换个关键词试试，或者切换其他分类看看。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setCat('all');
                }}
                className="mt-6 inline-flex h-10 items-center whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}